import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminPromoCodes from '../components/AdminPromoCodes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminPage() {
  const [activeTab, setActiveTab] = useState('stats');
  const [photos, setPhotos] = useState([]);
  const [users, setUsers] = useState([]);
  const [violations, setViolations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [suspiciousUsers, setSuspiciousUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showLogin, setShowLogin] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      verifyAdminSession(adminToken);
    }
  }, []);

  const verifyAdminSession = async (token) => {
    try {
      await axios.get(`${BACKEND_URL}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setIsAdmin(true);
      setShowLogin(false);
      fetchAllData(token);
    } catch (err) {
      localStorage.removeItem('adminToken');
      setShowLogin(true);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        email: adminEmail,
        password: adminPassword
      }, { withCredentials: true });

      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.admin_token);
        setIsAdmin(true);
        setShowLogin(false);
        fetchAllData(response.data.admin_token);
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'حدث خطأ في تسجيل الدخول');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAdmin(false);
    setShowLogin(true);
    setAdminEmail('');
    setAdminPassword('');
  };

  const fetchAllData = async (token) => {
    const adminToken = token || localStorage.getItem('adminToken');
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    
    setLoading(true);
    try {
      const [statsRes, photosRes, usersRes, violationsRes, alertsRes, suspiciousRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/photos`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/violations`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/alerts`, { headers, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/suspicious-users`, { headers, withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setPhotos(photosRes.data.photos || []);
      setUsers(usersRes.data.users || []);
      setViolations(violationsRes.data.violations || []);
      setAlerts(alertsRes.data.alerts || []);
      setSuspiciousUsers(suspiciousRes.data.suspicious_users || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  // User Actions
  const handleBanUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حظر هذا المستخدم؟')) return;
    setActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/ban`, {}, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      fetchAllData();
      alert('تم حظر المستخدم بنجاح');
    } catch (err) {
      alert('حدث خطأ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnbanUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/unban`, {}, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      fetchAllData();
      alert('تم إلغاء حظر المستخدم');
    } catch (err) {
      alert('حدث خطأ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`هل أنت متأكد من حذف المستخدم "${userName}" نهائياً؟ لا يمكن التراجع عن هذا الإجراء!`)) return;
    setActionLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      fetchAllData();
      setShowUserModal(false);
      alert('تم حذف المستخدم بنجاح');
    } catch (err) {
      alert('حدث خطأ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendUser = async (userId, days = 7) => {
    const reason = prompt('سبب التعليق:');
    if (!reason) return;
    setActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/suspend`, 
        { days, reason }, 
        { headers: getAuthHeaders(), withCredentials: true }
      );
      fetchAllData();
      alert(`تم تعليق الحساب لمدة ${days} أيام`);
    } catch (err) {
      alert('حدث خطأ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMakePremium = async (userId) => {
    const days = prompt('عدد أيام العضوية المميزة:', '30');
    if (!days) return;
    setActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/make-premium`, 
        { days: parseInt(days) }, 
        { headers: getAuthHeaders(), withCredentials: true }
      );
      fetchAllData();
      alert(`تم منح العضوية المميزة لمدة ${days} يوم`);
    } catch (err) {
      alert('حدث خطأ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemovePremium = async (userId) => {
    if (!window.confirm('هل أنت متأكد من إلغاء العضوية المميزة؟')) return;
    setActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/remove-premium`, {}, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      fetchAllData();
      alert('تم إلغاء العضوية المميزة');
    } catch (err) {
      alert('حدث خطأ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetWarnings = async (userId) => {
    setActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/reset-warnings`, {}, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      fetchAllData();
      alert('تم إعادة تعيين التحذيرات');
    } catch (err) {
      alert('حدث خطأ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // إضافة أعضاء وهميين
  const handleSeedFakeMembers = async (gender) => {
    const genderLabel = gender === 'أنثى' ? 'إناث' : 'ذكور';
    if (!window.confirm(`هل تريد إضافة 10 أعضاء وهميين (${genderLabel}) لإظهار الموقع نشطاً؟`)) return;
    setActionLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/seed-fake-members`, 
        { gender }, 
        { headers: getAuthHeaders(), withCredentials: true }
      );
      fetchAllData();
      alert(`✅ ${response.data.message}\n\nتمت الإضافة: ${response.data.added}\nتم التخطي (موجود مسبقاً): ${response.data.skipped}`);
    } catch (err) {
      alert('حدث خطأ: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Photo Actions
  const handleApprovePhoto = async (photoId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/photos/${photoId}/approve`, {}, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      fetchAllData();
    } catch (err) {
      alert('حدث خطأ');
    }
  };

  const handleRejectPhoto = async (photoId) => {
    if (!window.confirm('هل أنت متأكد من رفض وحذف هذه الصورة؟')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/photos/${photoId}/reject`, {}, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      fetchAllData();
    } catch (err) {
      alert('حدث خطأ');
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}/details`, { 
        headers: getAuthHeaders(),
        withCredentials: true 
      });
      setSelectedUser(response.data);
      setShowUserModal(true);
    } catch (err) {
      alert('حدث خطأ في جلب بيانات المستخدم');
    }
  };

  // Filter users by search
  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_id?.includes(searchTerm)
  );

  // Login Page
  if (showLogin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '40px',
          maxWidth: '400px',
          width: '100%',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '50px', marginBottom: '16px' }}>🔐</div>
            <h1 style={{ color: 'white', fontSize: '28px', marginBottom: '8px' }}>لوحة التحكم</h1>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>وِداد - إدارة الموقع</p>
          </div>

          {loginError && (
            <div style={{
              background: 'rgba(255, 87, 87, 0.2)',
              border: '1px solid rgba(255, 87, 87, 0.5)',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '20px',
              color: '#ff5757',
              textAlign: 'center',
              fontSize: '14px'
            }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleAdminLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                البريد الإلكتروني
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  fontSize: '16px'
                }}
                placeholder="admin@wedad.app"
                required
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                كلمة المرور
              </label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  fontSize: '16px'
                }}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #FF6B9D, #C06EED)',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: loginLoading ? 'wait' : 'pointer'
              }}
            >
              {loginLoading ? '⏳ جاري التحقق...' : '🔓 دخول'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div style={{ minHeight: '100vh', background: '#0f0f1a' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        padding: '20px 30px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '32px' }}>🛡️</span>
          <div>
            <h1 style={{ color: 'white', fontSize: '24px', margin: 0 }}>لوحة تحكم وِداد</h1>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: 0 }}>إدارة شاملة للموقع</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          style={{
            background: 'rgba(255,87,87,0.2)',
            border: '1px solid rgba(255,87,87,0.5)',
            color: '#ff5757',
            padding: '10px 20px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          🚪 تسجيل خروج
        </button>
      </div>

      <div style={{ display: 'flex' }}>
        {/* Sidebar */}
        <div style={{
          width: '250px',
          background: 'rgba(255,255,255,0.02)',
          borderLeft: '1px solid rgba(255,255,255,0.1)',
          minHeight: 'calc(100vh - 80px)',
          padding: '20px 0'
        }}>
          {[
            { id: 'stats', icon: '📊', label: 'الإحصائيات' },
            { id: 'users', icon: '👥', label: 'الأعضاء' },
            { id: 'photos', icon: '📷', label: 'الصور' },
            { id: 'violations', icon: '⚠️', label: 'المخالفات' },
            { id: 'alerts', icon: '🔔', label: 'التنبيهات', badge: alerts.length },
            { id: 'suspicious', icon: '🚨', label: 'المشبوهين', badge: suspiciousUsers.length },
            { id: 'promos', icon: '🎁', label: 'أكواد الخصم' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                width: '100%',
                padding: '14px 24px',
                background: activeTab === tab.id ? 'rgba(255, 107, 157, 0.2)' : 'transparent',
                border: 'none',
                borderRight: activeTab === tab.id ? '3px solid #FF6B9D' : '3px solid transparent',
                color: activeTab === tab.id ? '#FF6B9D' : 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                textAlign: 'right'
              }}
            >
              <span style={{ fontSize: '20px' }}>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.badge > 0 && (
                <span style={{
                  background: '#FF5757',
                  color: 'white',
                  borderRadius: '10px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  marginRight: 'auto'
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: '30px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.6)' }}>
              <div style={{ fontSize: '50px', marginBottom: '16px' }}>⏳</div>
              <p>جاري التحميل...</p>
            </div>
          ) : (
            <>
              {/* Stats Tab */}
              {activeTab === 'stats' && (
                <div>
                  <h2 style={{ color: 'white', marginBottom: '24px', fontSize: '22px' }}>📊 إحصائيات الموقع</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                    {[
                      { label: 'إجمالي الأعضاء', value: stats.total_users || 0, icon: '👥', color: '#4facfe' },
                      { label: 'أعضاء مميزين', value: stats.premium_users || 0, icon: '⭐', color: '#FFD700' },
                      { label: 'أعضاء جدد اليوم', value: stats.new_users_today || 0, icon: '🆕', color: '#00D9A5' },
                      { label: 'الرجال', value: stats.male_users || 0, icon: '👨', color: '#667eea' },
                      { label: 'النساء', value: stats.female_users || 0, icon: '👩', color: '#f093fb' },
                      { label: 'موثقين', value: stats.verified_users || 0, icon: '✅', color: '#00B388' },
                      { label: 'محظورين', value: stats.banned_users || 0, icon: '🚫', color: '#FF5757' },
                      { label: 'تحذيرات', value: stats.users_with_warnings || 0, icon: '⚠️', color: '#FFA500' },
                      { label: 'صور معلقة', value: stats.pending_photos || 0, icon: '📷', color: '#C06EED' },
                      { label: 'المخالفات', value: stats.total_violations || 0, icon: '🚨', color: '#FF5757' },
                      { label: 'الرسائل', value: stats.total_messages || 0, icon: '💬', color: '#4facfe' },
                      { label: 'الارتباطات', value: stats.total_connections || 0, icon: '💕', color: '#FF6B9D' }
                    ].map((stat, i) => (
                      <div key={i} style={{
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: '16px',
                        padding: '24px',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <div style={{ fontSize: '36px', marginBottom: '12px' }}>{stat.icon}</div>
                        <div style={{ fontSize: '32px', fontWeight: '800', color: stat.color }}>{stat.value}</div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* أدوات سريعة */}
                  <div style={{ marginTop: '32px', padding: '24px', background: 'rgba(255,255,255,0.05)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <h3 style={{ color: 'white', marginBottom: '16px', fontSize: '18px' }}>🛠️ أدوات سريعة</h3>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleSeedFakeMembers('أنثى')}
                        disabled={actionLoading}
                        style={{
                          background: 'linear-gradient(135deg, #f093fb, #f5576c)',
                          color: 'white',
                          border: 'none',
                          padding: '14px 24px',
                          borderRadius: '12px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: actionLoading ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        👩 إضافة 10 إناث وهميين
                      </button>
                      <button
                        onClick={() => handleSeedFakeMembers('ذكر')}
                        disabled={actionLoading}
                        style={{
                          background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: 'white',
                          border: 'none',
                          padding: '14px 24px',
                          borderRadius: '12px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: actionLoading ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        👨 إضافة 10 ذكور وهميين
                      </button>
                      <button
                        onClick={() => fetchAllData()}
                        disabled={loading}
                        style={{
                          background: 'linear-gradient(135deg, #4facfe, #00f2fe)',
                          color: 'white',
                          border: 'none',
                          padding: '14px 24px',
                          borderRadius: '12px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: loading ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        🔄 تحديث البيانات
                      </button>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginTop: '12px' }}>
                      💡 أزرار إضافة الأعضاء الوهميين تضيف 10 حسابات بمواصفات متنوعة لإظهار الموقع نشطاً
                    </p>
                  </div>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2 style={{ color: 'white', fontSize: '22px', margin: 0 }}>👥 إدارة الأعضاء ({users.length})</h2>
                    <input
                      type="text"
                      placeholder="🔍 بحث بالاسم أو الإيميل..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.05)',
                        color: 'white',
                        width: '300px'
                      }}
                    />
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
                          {['الاسم', 'الإيميل', 'الجنس', 'الحالة', 'العضوية', 'الإجراءات'].map(h => (
                            <th key={h} style={{ 
                              padding: '14px', 
                              color: 'rgba(255,255,255,0.8)', 
                              textAlign: 'right',
                              fontWeight: '600',
                              borderBottom: '1px solid rgba(255,255,255,0.1)'
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(user => (
                          <tr key={user.user_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <td style={{ padding: '14px', color: 'white' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '24px' }}>{user.gender === 'male' ? '👨' : '👩'}</span>
                                <div>
                                  <div style={{ fontWeight: '600' }}>{user.name || 'بدون اسم'}</div>
                                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{user.user_id}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '14px', color: 'rgba(255,255,255,0.7)', direction: 'ltr', textAlign: 'right' }}>{user.email}</td>
                            <td style={{ padding: '14px', color: 'rgba(255,255,255,0.7)' }}>{user.gender === 'male' ? 'ذكر' : 'أنثى'}</td>
                            <td style={{ padding: '14px' }}>
                              {user.is_banned ? (
                                <span style={{ background: '#FF5757', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>محظور</span>
                              ) : user.is_suspended ? (
                                <span style={{ background: '#FFA500', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>معلق</span>
                              ) : (
                                <span style={{ background: '#00D9A5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>نشط</span>
                              )}
                            </td>
                            <td style={{ padding: '14px' }}>
                              {user.membership_type === 'premium' ? (
                                <span style={{ background: '#FFD700', color: '#333', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>⭐ مميز</span>
                              ) : (
                                <span style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>مجاني</span>
                              )}
                            </td>
                            <td style={{ padding: '14px' }}>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button onClick={() => viewUserDetails(user.user_id)} style={actionBtnStyle('#4facfe')}>👁️</button>
                                {user.is_banned ? (
                                  <button onClick={() => handleUnbanUser(user.user_id)} style={actionBtnStyle('#00D9A5')}>✅</button>
                                ) : (
                                  <button onClick={() => handleBanUser(user.user_id)} style={actionBtnStyle('#FF5757')}>🚫</button>
                                )}
                                <button onClick={() => handleSuspendUser(user.user_id, 7)} style={actionBtnStyle('#FFA500')}>⏸️</button>
                                {user.membership_type === 'premium' ? (
                                  <button onClick={() => handleRemovePremium(user.user_id)} style={actionBtnStyle('#666')}>⭐❌</button>
                                ) : (
                                  <button onClick={() => handleMakePremium(user.user_id)} style={actionBtnStyle('#FFD700')}>⭐</button>
                                )}
                                <button onClick={() => handleDeleteUser(user.user_id, user.name)} style={actionBtnStyle('#FF0000')}>🗑️</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Photos Tab */}
              {activeTab === 'photos' && (
                <div>
                  <h2 style={{ color: 'white', marginBottom: '24px', fontSize: '22px' }}>📷 مراجعة الصور ({photos.filter(p => !p.approved).length} معلقة)</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                    {photos.filter(p => !p.approved).map(photo => (
                      <div key={photo.photo_id} style={{
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <img 
                          src={`${BACKEND_URL}/api/photos/file/${photo.filename}`} 
                          alt="Photo"
                          style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                        />
                        <div style={{ padding: '16px' }}>
                          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '12px' }}>
                            {photo.type === 'selfie' ? '🤳 صورة شخصية' : '📷 صورة عادية'}
                          </p>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => handleApprovePhoto(photo.photo_id)} style={{
                              flex: 1, padding: '10px', background: '#00D9A5', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
                            }}>✅ موافقة</button>
                            <button onClick={() => handleRejectPhoto(photo.photo_id)} style={{
                              flex: 1, padding: '10px', background: '#FF5757', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
                            }}>❌ رفض</button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {photos.filter(p => !p.approved).length === 0 && (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.5)' }}>
                        <div style={{ fontSize: '60px', marginBottom: '16px' }}>✅</div>
                        <p>لا توجد صور معلقة للمراجعة</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Violations Tab */}
              {activeTab === 'violations' && (
                <div>
                  <h2 style={{ color: 'white', marginBottom: '24px', fontSize: '22px' }}>⚠️ المخالفات ({violations.length})</h2>
                  {violations.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.5)' }}>
                      <div style={{ fontSize: '60px', marginBottom: '16px' }}>🎉</div>
                      <p>لا توجد مخالفات مسجلة</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {violations.map(v => (
                        <div key={v.violation_id} style={{
                          background: 'rgba(255,87,87,0.1)',
                          border: '1px solid rgba(255,87,87,0.3)',
                          borderRadius: '12px',
                          padding: '16px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#FF5757', fontWeight: '600' }}>{v.type}</span>
                            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{new Date(v.created_at).toLocaleDateString('ar')}</span>
                          </div>
                          <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0 }}>{v.details}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Alerts Tab */}
              {activeTab === 'alerts' && (
                <div>
                  <h2 style={{ color: 'white', marginBottom: '24px', fontSize: '22px' }}>🔔 التنبيهات ({alerts.length})</h2>
                  {alerts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.5)' }}>
                      <div style={{ fontSize: '60px', marginBottom: '16px' }}>🔕</div>
                      <p>لا توجد تنبيهات جديدة</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {alerts.map(a => (
                        <div key={a.alert_id} style={{
                          background: 'rgba(255,165,0,0.1)',
                          border: '1px solid rgba(255,165,0,0.3)',
                          borderRadius: '12px',
                          padding: '16px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ color: '#FFA500', fontWeight: '600' }}>{a.type}</span>
                            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{new Date(a.created_at).toLocaleDateString('ar')}</span>
                          </div>
                          <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0 }}>{a.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Suspicious Users Tab */}
              {activeTab === 'suspicious' && (
                <div>
                  <h2 style={{ color: 'white', marginBottom: '24px', fontSize: '22px' }}>🚨 المستخدمين المشبوهين ({suspiciousUsers.length})</h2>
                  {suspiciousUsers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.5)' }}>
                      <div style={{ fontSize: '60px', marginBottom: '16px' }}>✅</div>
                      <p>لا يوجد مستخدمين مشبوهين</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {suspiciousUsers.map(user => (
                        <div key={user.user_id} style={{
                          background: 'rgba(255,87,87,0.1)',
                          border: '1px solid rgba(255,87,87,0.3)',
                          borderRadius: '12px',
                          padding: '16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <div style={{ color: 'white', fontWeight: '600' }}>{user.name}</div>
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{user.email}</div>
                            <div style={{ color: '#FF5757', fontSize: '13px', marginTop: '4px' }}>
                              ⚠️ {user.violations_count} مخالفة
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => handleResetWarnings(user.user_id)} style={actionBtnStyle('#00D9A5')}>🔄</button>
                            <button onClick={() => handleBanUser(user.user_id)} style={actionBtnStyle('#FF5757')}>🚫</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Promo Codes Tab */}
              {activeTab === 'promos' && <AdminPromoCodes />}
            </>
          )}
        </div>
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#1a1a2e',
            borderRadius: '20px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={{ padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ color: 'white', margin: 0 }}>👤 تفاصيل المستخدم</h3>
              <button onClick={() => setShowUserModal(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>الاسم:</span> <span style={{ color: 'white' }}>{selectedUser.user?.name}</span></div>
                <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>الإيميل:</span> <span style={{ color: 'white' }}>{selectedUser.user?.email}</span></div>
                <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>الجنس:</span> <span style={{ color: 'white' }}>{selectedUser.user?.gender === 'male' ? 'ذكر' : 'أنثى'}</span></div>
                <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>العمر:</span> <span style={{ color: 'white' }}>{selectedUser.user?.age || '-'}</span></div>
                <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>المدينة:</span> <span style={{ color: 'white' }}>{selectedUser.user?.city || '-'}</span></div>
                <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>الرسائل:</span> <span style={{ color: 'white' }}>{selectedUser.messages_count}</span></div>
                <div><span style={{ color: 'rgba(255,255,255,0.5)' }}>الارتباطات:</span> <span style={{ color: 'white' }}>{selectedUser.connections_count}</span></div>
              </div>
              
              <div style={{ marginTop: '24px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => handleDeleteUser(selectedUser.user?.user_id, selectedUser.user?.name)} disabled={actionLoading}
                  style={{ padding: '12px 20px', background: '#FF5757', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                  🗑️ حذف نهائي
                </button>
                <button onClick={() => handleBanUser(selectedUser.user?.user_id)} disabled={actionLoading}
                  style={{ padding: '12px 20px', background: '#FF5757', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                  🚫 حظر
                </button>
                <button onClick={() => handleMakePremium(selectedUser.user?.user_id)} disabled={actionLoading}
                  style={{ padding: '12px 20px', background: '#FFD700', color: '#333', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                  ⭐ منح Premium
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const actionBtnStyle = (color) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  border: 'none',
  background: color,
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px'
});

export default AdminPage;
